
import React, { useState, Fragment } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Dialog, Transition } from '@headlessui/react';
import { toast } from 'react-hot-toast';
import {
    UserPlusIcon,
    TrashIcon,
    PencilSquareIcon,
    CheckCircleIcon,
    XCircleIcon,
    BuildingOfficeIcon,
    XMarkIcon
} from '@heroicons/react/24/outline';
import apiClient from '../../services/apiClient';

interface User {
    id: string;
    name: string;
    email: string;
    customers: string[]; // customer IDs
    isActive: boolean;
    role: string;
    isConsultant: boolean;
}


const UserManagement: React.FC = () => {
    const queryClient = useQueryClient();
    const [isInviteOpen, setIsInviteOpen] = useState(false);
    const [isAssignOpen, setIsAssignOpen] = useState(false);
    const [selectedUser, setSelectedUser] = useState<User | null>(null);

    // Invite form state
    const [inviteEmail, setInviteEmail] = useState('');
    const [inviteName, setInviteName] = useState('');

    // Assign form state
    const [selectedCustomerIds, setSelectedCustomerIds] = useState<string[]>([]);
    const [isConsultant, setIsConsultant] = useState(false);

    // Filter state
    const [filterName, setFilterName] = useState('');
    const [filterRole, setFilterRole] = useState('');
    const [filterCustomer, setFilterCustomer] = useState('');
    const [filterConsultant, setFilterConsultant] = useState<'all' | 'yes' | 'no'>('all');
    const [filterStatus, setFilterStatus] = useState<'all' | 'active' | 'inactive'>('all');

    // Fetch users
    const { data: users = [], isLoading: isLoadingUsers } = useQuery({
        queryKey: ['users'],
        queryFn: async () => apiClient.getUsers(),
    });

    // Fetch customers (for assignment)
    const { data: customers = [] } = useQuery({
        queryKey: ['customers'],
        queryFn: async () => apiClient.getCustomers(),
    });

    // Filter users based on filter criteria
    const filteredUsers = users.filter((user: User) => {
        // Name/Email filter
        if (filterName && !user.name.toLowerCase().includes(filterName.toLowerCase()) &&
            !user.email.toLowerCase().includes(filterName.toLowerCase())) {
            return false;
        }

        // Role filter
        if (filterRole && !user.role.toLowerCase().includes(filterRole.toLowerCase())) {
            return false;
        }

        // Customer filter
        if (filterCustomer) {
            const userCustomerNames = user.customers?.map(custId => {
                const customer = customers.find((c: any) => c.id === custId);
                return customer?.name || '';
            }).join(' ').toLowerCase();

            if (!userCustomerNames.includes(filterCustomer.toLowerCase())) {
                return false;
            }
        }

        // Consultant filter
        if (filterConsultant === 'yes' && !user.isConsultant) return false;
        if (filterConsultant === 'no' && user.isConsultant) return false;

        // Status filter
        if (filterStatus === 'active' && !user.isActive) return false;
        if (filterStatus === 'inactive' && user.isActive) return false;

        return true;
    });

    // Mutations
    const inviteMutation = useMutation({
        mutationFn: async (data: { email: string; name: string }) => {
            return apiClient.inviteUser(data.email, data.name);
        },
        onSuccess: async () => {
            await queryClient.invalidateQueries({ queryKey: ['users'] });
            await queryClient.refetchQueries({ queryKey: ['users'] });
            toast.success('User invited successfully');
            setIsInviteOpen(false);
            setInviteEmail('');
            setInviteName('');
        },
        onError: (error) => {
            toast.error('Failed to invite user');
            console.error(error);
        }
    });

    const updateMutation = useMutation({
        mutationFn: async (data: { userId: string; payload: any }) => {
            return apiClient.updateUser(data.userId, data.payload);
        },
        onSuccess: async () => {
            await queryClient.invalidateQueries({ queryKey: ['users'] });
            await queryClient.refetchQueries({ queryKey: ['users'] });
            toast.success('User updated successfully');
            setIsAssignOpen(false);
        },
        onError: (error) => {
            toast.error('Failed to update user');
            console.error(error);
        }
    });

    const handleInvite = (e: React.FormEvent) => {
        e.preventDefault();
        if (!inviteEmail || !inviteName) return;
        inviteMutation.mutate({ email: inviteEmail, name: inviteName });
    };

    const handleOpenAssign = (user: User) => {
        setSelectedUser(user);
        setSelectedCustomerIds(user.customers || []);
        setIsConsultant(user.isConsultant || false);
        setIsAssignOpen(true);
    };

    const handleAssignSubmit = () => {
        if (!selectedUser) return;
        updateMutation.mutate({
            userId: selectedUser.id,
            payload: {
                customers: selectedCustomerIds,
                isConsultant: isConsultant
            }
        });
    };

    const toggleUserStatus = (user: User) => {
        if (!confirm(`Are you sure you want to ${user.isActive ? 'deactivate' : 'activate'} this user?`)) return;
        updateMutation.mutate({
            userId: user.id,
            payload: { isActive: !user.isActive }
        });
    };

    const toggleCustomerSelection = (customerId: string) => {
        setSelectedCustomerIds(prev =>
            prev.includes(customerId)
                ? prev.filter(id => id !== customerId)
                : [...prev, customerId]
        );
    };

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center border-b border-gray-200 pb-5">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900">User Management</h1>
                    <p className="mt-1 text-sm text-gray-500">Manage user access and customer assignments</p>
                </div>
                <button
                    onClick={() => setIsInviteOpen(true)}
                    className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors"
                >
                    <UserPlusIcon className="-ml-1 mr-2 h-5 w-5" aria-hidden="true" />
                    Invite User
                </button>
            </div>

            {isLoadingUsers ? (
                <div className="text-center py-10">
                    <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-600 mx-auto"></div>
                    <p className="mt-2 text-sm text-gray-500">Loading users...</p>
                </div>
            ) : (
                <div className="bg-white shadow overflow-hidden rounded-lg border border-gray-200">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    User
                                </th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Role
                                </th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Assigned Customers
                                </th>
                                <th scope="col" className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Consultant
                                </th>
                                <th scope="col" className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Status
                                </th>
                                <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Actions
                                </th>
                            </tr>
                            <tr className="bg-gray-100">
                                <th scope="col" className="px-6 py-2">
                                    <input
                                        type="text"
                                        placeholder="Filter by name or email..."
                                        value={filterName}
                                        onChange={(e) => setFilterName(e.target.value)}
                                        className="w-full px-2 py-1 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-500"
                                    />
                                </th>
                                <th scope="col" className="px-6 py-2">
                                    <input
                                        type="text"
                                        placeholder="Filter by role..."
                                        value={filterRole}
                                        onChange={(e) => setFilterRole(e.target.value)}
                                        className="w-full px-2 py-1 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-500"
                                    />
                                </th>
                                <th scope="col" className="px-6 py-2">
                                    <input
                                        type="text"
                                        placeholder="Filter by customer..."
                                        value={filterCustomer}
                                        onChange={(e) => setFilterCustomer(e.target.value)}
                                        className="w-full px-2 py-1 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-500"
                                    />
                                </th>
                                <th scope="col" className="px-6 py-2">
                                    <select
                                        value={filterConsultant}
                                        onChange={(e) => setFilterConsultant(e.target.value as 'all' | 'yes' | 'no')}
                                        className="w-full px-2 py-1 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-500"
                                    >
                                        <option value="all">All</option>
                                        <option value="yes">Yes</option>
                                        <option value="no">No</option>
                                    </select>
                                </th>
                                <th scope="col" className="px-6 py-2">
                                    <select
                                        value={filterStatus}
                                        onChange={(e) => setFilterStatus(e.target.value as 'all' | 'active' | 'inactive')}
                                        className="w-full px-2 py-1 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-500"
                                    >
                                        <option value="all">All</option>
                                        <option value="active">Active</option>
                                        <option value="inactive">Inactive</option>
                                    </select>
                                </th>
                                <th scope="col" className="px-6 py-2">
                                    {/* Empty cell for Actions column */}
                                </th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {filteredUsers.map((user: User) => (
                                <tr key={user.id} className="hover:bg-gray-50 transition-colors">
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="flex items-center">
                                            <div className="h-10 w-10 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-700 font-bold text-lg">
                                                {user.name.charAt(0).toUpperCase()}
                                            </div>
                                            <div className="ml-4">
                                                <div className="text-sm font-medium text-gray-900">{user.name}</div>
                                                <div className="text-sm text-gray-500">{user.email}</div>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                            {user.role}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4">
                                        <div className="flex flex-wrap gap-1">
                                            {user.customers?.length > 0 ? (
                                                user.customers.map(custId => {
                                                    const customer = customers.find((c: any) => c.id === custId);
                                                    return (
                                                        <span key={custId} className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800">
                                                            {customer?.name || 'Unknown'}
                                                        </span>
                                                    );
                                                })
                                            ) : (
                                                <span className="text-sm text-gray-400 italic">No customers assigned</span>
                                            )}
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-center">
                                        {user.isConsultant ? (
                                            <CheckCircleIcon className="h-5 w-5 text-green-500 mx-auto" />
                                        ) : (
                                            <XCircleIcon className="h-5 w-5 text-gray-300 mx-auto" />
                                        )}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-center">
                                        {user.isActive ? (
                                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                                Active
                                            </span>
                                        ) : (
                                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                                Inactive
                                            </span>
                                        )}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                        <div className="flex items-center justify-end space-x-3">
                                            <button
                                                onClick={() => handleOpenAssign(user)}
                                                disabled={user.role === 'Admin'}
                                                className={`flex items-center ${user.role === 'Admin' ? 'text-gray-400 cursor-not-allowed' : 'text-indigo-600 hover:text-indigo-900'}`}
                                                title={user.role === 'Admin' ? 'Admin role is managed in Entra ID only' : 'Assign Customers'}
                                            >
                                                <BuildingOfficeIcon className="h-5 w-5" />
                                            </button>
                                            <button
                                                onClick={() => toggleUserStatus(user)}
                                                disabled={user.role === 'Admin'}
                                                className={`flex items-center ${user.role === 'Admin' ? 'text-gray-400 cursor-not-allowed' : user.isActive ? 'text-red-600 hover:text-red-900' : 'text-green-600 hover:text-green-900'}`}
                                                title={user.role === 'Admin' ? 'Admin users cannot be deactivated from UI' : user.isActive ? "Deactivate" : "Activate"}
                                            >
                                                {user.isActive ? (
                                                    <XCircleIcon className="h-5 w-5" />
                                                ) : (
                                                    <CheckCircleIcon className="h-5 w-5" />
                                                )}
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                            {filteredUsers.length === 0 && (
                                <tr>
                                    <td colSpan={6} className="px-6 py-10 text-center text-gray-500">
                                        No users found matching the filters.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Invite User Modal */}
            <Transition appear show={isInviteOpen} as={Fragment}>
                <Dialog as="div" className="relative z-10" onClose={() => setIsInviteOpen(false)}>
                    <Transition.Child
                        as={Fragment}
                        enter="ease-out duration-300"
                        enterFrom="opacity-0"
                        enterTo="opacity-100"
                        leave="ease-in duration-200"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0"
                    >
                        <div className="fixed inset-0 bg-black bg-opacity-25" />
                    </Transition.Child>

                    <div className="fixed inset-0 overflow-y-auto">
                        <div className="flex min-h-full items-center justify-center p-4 text-center">
                            <Transition.Child
                                as={Fragment}
                                enter="ease-out duration-300"
                                enterFrom="opacity-0 scale-95"
                                enterTo="opacity-100 scale-100"
                                leave="ease-in duration-200"
                                leaveFrom="opacity-100 scale-100"
                                leaveTo="opacity-0 scale-95"
                            >
                                <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                                    <div className="flex justify-between items-start mb-4">
                                        <Dialog.Title as="h3" className="text-lg font-medium leading-6 text-gray-900">
                                            Invite New User
                                        </Dialog.Title>
                                        <button onClick={() => setIsInviteOpen(false)} className="text-gray-400 hover:text-gray-500">
                                            <XMarkIcon className="h-6 w-6" />
                                        </button>
                                    </div>
                                    <form onSubmit={handleInvite}>
                                        <div className="space-y-4">
                                            <div>
                                                <label htmlFor="name" className="block text-sm font-medium text-gray-700">Full Name</label>
                                                <input
                                                    type="text"
                                                    id="name"
                                                    value={inviteName}
                                                    onChange={(e) => setInviteName(e.target.value)}
                                                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm border p-2"
                                                    placeholder="John Doe"
                                                    required
                                                />
                                            </div>
                                            <div>
                                                <label htmlFor="email" className="block text-sm font-medium text-gray-700">Email Address</label>
                                                <input
                                                    type="email"
                                                    id="email"
                                                    value={inviteEmail}
                                                    onChange={(e) => setInviteEmail(e.target.value)}
                                                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm border p-2"
                                                    placeholder="john@example.com"
                                                    required
                                                />
                                            </div>
                                        </div>

                                        <div className="mt-8 flex justify-end space-x-3">
                                            <button
                                                type="button"
                                                onClick={() => setIsInviteOpen(false)}
                                                className="inline-flex justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                                            >
                                                Cancel
                                            </button>
                                            <button
                                                type="submit"
                                                disabled={inviteMutation.isPending}
                                                className="inline-flex justify-center rounded-md border border-transparent bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:opacity-50"
                                            >
                                                {inviteMutation.isPending ? 'Inviting...' : 'Send Invitation'}
                                            </button>
                                        </div>
                                    </form>
                                </Dialog.Panel>
                            </Transition.Child>
                        </div>
                    </div>
                </Dialog>
            </Transition>

            {/* Assign Customers Modal */}
            <Transition appear show={isAssignOpen} as={Fragment}>
                <Dialog as="div" className="relative z-10" onClose={() => setIsAssignOpen(false)}>
                    <Transition.Child
                        as={Fragment}
                        enter="ease-out duration-300"
                        enterFrom="opacity-0"
                        enterTo="opacity-100"
                        leave="ease-in duration-200"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0"
                    >
                        <div className="fixed inset-0 bg-black bg-opacity-25" />
                    </Transition.Child>

                    <div className="fixed inset-0 overflow-y-auto">
                        <div className="flex min-h-full items-center justify-center p-4 text-center">
                            <Transition.Child
                                as={Fragment}
                                enter="ease-out duration-300"
                                enterFrom="opacity-0 scale-95"
                                enterTo="opacity-100 scale-100"
                                leave="ease-in duration-200"
                                leaveFrom="opacity-100 scale-100"
                                leaveTo="opacity-0 scale-95"
                            >
                                <Dialog.Panel className="w-full max-w-lg transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                                    <div className="flex justify-between items-start mb-4">
                                        <Dialog.Title as="h3" className="text-lg font-medium leading-6 text-gray-900">
                                            Assign Customers to {selectedUser?.name}
                                        </Dialog.Title>
                                        {selectedUser?.role === 'Admin' && (
                                            <div className="mt-2 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
                                                <p className="text-sm text-yellow-800">
                                                    <strong>Note:</strong> This user has the 'admin' role which is managed in Entra ID. Customer assignments are not applicable for admin users.
                                                </p>
                                            </div>
                                        )}
                                        <button onClick={() => setIsAssignOpen(false)} className="text-gray-400 hover:text-gray-500">
                                            <XMarkIcon className="h-6 w-6" />
                                        </button>
                                    </div>

                                    <div className="mt-4 mb-4 bg-gray-50 p-3 rounded-md border border-gray-200">
                                        <div className="flex items-center justify-between">
                                            <div className="flex items-center">
                                                <input
                                                    id="is-consultant"
                                                    name="is-consultant"
                                                    type="checkbox"
                                                    checked={isConsultant}
                                                    onChange={(e) => setIsConsultant(e.target.checked)}
                                                    className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                                                />
                                                <label htmlFor="is-consultant" className="ml-2 block text-sm font-medium text-gray-900">
                                                    Is Consultant?
                                                </label>
                                            </div>
                                            <p className="text-xs text-gray-500 max-w-[250px] text-right">
                                                Grants "Consultant" role for selected customers and static "2S Consultant" role.
                                            </p>
                                        </div>
                                    </div>

                                    <div className="mt-4 max-h-96 overflow-y-auto border rounded-md p-2 space-y-2">
                                        {customers.map((customer: any) => (
                                            <div
                                                key={customer.id}
                                                className={`flex items-center p-3 rounded-md cursor-pointer transition-colors ${selectedCustomerIds.includes(customer.id)
                                                    ? 'bg-indigo-50 border border-indigo-200'
                                                    : 'hover:bg-gray-50 border border-transparent'
                                                    }`}
                                                onClick={() => toggleCustomerSelection(customer.id)}
                                            >
                                                <input
                                                    type="checkbox"
                                                    checked={selectedCustomerIds.includes(customer.id)}
                                                    onChange={() => { }} // handled by div click
                                                    className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                                                />
                                                <span className="ml-3 block text-sm font-medium text-gray-700">
                                                    {customer.name}
                                                </span>
                                            </div>
                                        ))}
                                        {customers.length === 0 && (
                                            <p className="text-sm text-gray-500 italic p-4 text-center">No customers available</p>
                                        )}
                                    </div>

                                    <div className="mt-8 flex justify-end space-x-3">
                                        <button
                                            type="button"
                                            onClick={() => setIsAssignOpen(false)}
                                            className="inline-flex justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            type="button"
                                            onClick={handleAssignSubmit}
                                            disabled={updateMutation.isPending}
                                            className="inline-flex justify-center rounded-md border border-transparent bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:opacity-50"
                                        >
                                            {updateMutation.isPending ? 'Saving...' : 'Save Assignments'}
                                        </button>
                                    </div>
                                </Dialog.Panel>
                            </Transition.Child>
                        </div>
                    </div>
                </Dialog>
            </Transition>
        </div>
    );
};

export default UserManagement;
